import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SelectDropDown from '../../../components/common/SelectDropDown';
import { strings } from '../../../localization';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize'; // "to continue using CSS stylesheets and classes" https://github.com/airbnb/react-dates
import FlagFilterDropDown from './FlagFilterDropDown';
import TagsTreeViewDropDown from '../../../components/TagsTreeViewDropDown';
import ResizeThrottler from '../../../components/ResizeThrottler';

const RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS = 100; // IMPORTANT: must be a greater value than RESIZE_EVENT_DISPATCH_INTERVAL_MS (togglePanels(...) triggers resize event directly)

const FILTER_OPTIONS_TOP_PADDING = 8.750 + 23.67 + 0.667 + 8.750; // .options-scroll-wrapper padding-top + .checkbox-filters input's height and margin and border
const MAX_WIDTH_FOR_DATE_SELECTOR = 576;

const FILTER_TYPES = {
    VEHICLES: 'vehicles',
    DRIVERS: 'drivers',
    EVENTS: 'events',
}

export class FilterGroupPanel extends React.PureComponent {

    constructor(props) {

        super(props);


        this.state = {
            windowWidth: window.innerWidth,
            dateFocusedInput: undefined,

            selectedTags: [],
            isEventFirstRender: true,
            isVehicleFirstRender: true,
            isDriverFirstRender: true,

            isVehiclesFilterExpanded: true,
            isDriversFilterExpanded: true,
            isEventsFilterExpanded: true,
            allowAllFiltersToExpand: true,
        };

        this.flagFilterDropdownRef = React.createRef();
        this.eventsFilterDropdownRef = React.createRef();
        this.vehiclesFilterDropdownRef = React.createRef();
        this.driversFilterDropdownRef = React.createRef();

        this.needsResizeThrottler = !window.ResizeObserver;
        this.resizeObserver = null;
    }

    componentDidMount() {

        if(window.ResizeObserver){
            // Create ResizeObserver to replace ResizeThrottler
            this.resizeObserver = new ResizeObserver(entries => {
                this.resizeCallback();
            });
            
            // Observe document root to catch window resizing
            this.resizeObserver.observe(document.documentElement);
            
            // Observe filter panel container
            const filterPanel = document.querySelector('.event-review-filter-group-panel');
            if (filterPanel) {
                this.resizeObserver.observe(filterPanel);
            }
        }
        
        // Initial calculation
        this.setDropdownHeight();
    }

    componentWillUnmount() {
        // Clean up the observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    resizeCallback = () => {
        // Update window width state
        this.setState({ windowWidth: window.innerWidth });
            
        // Adjust dropdown heights
        this.setDropdownHeight();

        // Handle filter expansion based on screen height
        if(window.innerHeight < 590) {
            // It's already applied. Don't do again.
            if(!this.state.allowAllFiltersToExpand) return;

            this.setState({ 
                isVehiclesFilterExpanded: false,
                isDriversFilterExpanded: false,
                isEventsFilterExpanded: false,
                allowAllFiltersToExpand: false,
            });
        }
        else {
            // It's already applied. Don't do again.
            if(this.state.allowAllFiltersToExpand) return;

            this.setState({ 
                isVehiclesFilterExpanded: true,
                isDriversFilterExpanded: true,
                isEventsFilterExpanded: true,
                allowAllFiltersToExpand: true,
            });
        }
    }

    setDropdownHeight = () => {

        // Handle all dropdowns with a common approach
        const adjustDropdown = (dropdownRef, hasFilterInput = true, dropdownBodySelector = '.options-scroll-wrapper') => {
            if (!dropdownRef || !dropdownRef.current) return;
            
            const dropdown = dropdownRef.current;

            const dropdownBody = dropdown.querySelector(dropdownBodySelector);
            if (!dropdownBody) return;
            

            const rect = dropdown.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;

            // Calculate available space below the dropdown
            let availableSpace = spaceBelow - 15; // 15px for margin from the bottom of the screen

            if(hasFilterInput) {
                availableSpace -= FILTER_OPTIONS_TOP_PADDING; // Adjust for the filter input inside the dropdown
            }
            
            // Set max height (with minimum to ensure usability)
            dropdownBody.style.maxHeight = `${Math.max(100, availableSpace)}px`;
        };
        
        // Apply to all dropdowns
        adjustDropdown(this.vehiclesFilterDropdownRef);
        adjustDropdown(this.driversFilterDropdownRef);
        adjustDropdown(this.eventsFilterDropdownRef);
        adjustDropdown(this.flagFilterDropdownRef, false, '.flag-checkboxes');
        
    }

    checkDateIsOutside = (day) => {
        return false;
    }

    filterVehiclesByTag = (selectedTags) => {
        this.setState({selectedTags});
        this.props.onTagsFilterOptionsSelect(selectedTags);
    }

    toggleTagsFilter = () => {
        this.setState({showTagsFilter: !this.state.showTagsFilter});
    }

    renderTagsFilter = () => {
        // There is no error, no loading and no loaded tags. So, can conclude Tags are not enabled.
        const tagsNotEnabled = !this.props.tagsLoaded && !this.props.tagsLoading && !this.props.tagsError;

        if(tagsNotEnabled){
            console.log('Events Review: Tags are not enabled');
            return null;
        }

        if(this.props.tagsError){
            console.error('Events Review: Error fetching tags');
            return null;
        }

        if(this.props.tagsLoading){
            return null;
        }

        if(this.props.tags.length === 0){
            return null;
        }

        return (
            <TagsTreeViewDropDown
                tags={this.props.tags}
                selectedTagsIds={this.state.selectedTags}
                placeholder={strings.tags_filter_by_tags}
                multipleSelection={true}
                onChange={this.filterVehiclesByTag}
                clearFilterTags={this.props.clearFilterTags}
                resetClearFilterTags={this.props.resetClearFilterTags}
                className="events-review-filter"
                showClearButton={false}
                cascadeSelection
                compact
            />
        );

    }

    componentDidUpdate(prevProps) {

        //auto-set filters by default
        // if (this.selectAllEventsButtonRef && this.state.isEventFirstRender) {
        //     setTimeout(() => {
        //         this.selectAllEventsButtonRef.click();
        //     }, 0);
        //     this.setState({ isEventFirstRender: false });
        // }
        // if (this.selectAllVehiclesButtonRef && this.state.isVehicleFirstRender) {
        //     setTimeout(() => {
        //         this.selectAllVehiclesButtonRef.click();
        //     }, 0);
        //     this.setState({ isVehicleFirstRender: false });
        // }
        // if (this.selectAllDriversButtonRef && this.state.isDriverFirstRender) {
        //     setTimeout(() => {
        //         this.selectAllDriversButtonRef.click();
        //     }, 0);
        //     this.setState({ isDriverFirstRender: false });
        // }

        // auto-click 'APPLY' button when sortOptions changes
        if (!this.props.applyIsDisabled && prevProps.sortOptions !== this.props.sortOptions) {
            setTimeout(() => {
                this.searchButtonRef.click();
            }, 0);
        }
    }

    changeSelection = (filterType, newValue = true) => {
    
        switch (filterType) {
            case FILTER_TYPES.VEHICLES:
                this.props.onVehiclesFiltersNamesOptionsSetAll(newValue);
                if(this.props.tags){
                    if(newValue === true){
                        this.filterVehiclesByTag(this.props.tags.map(tag => tag.tagId));
                    }
                    else{
                        this.filterVehiclesByTag([]);
                    }
                }
                break;
            case FILTER_TYPES.DRIVERS:
                this.props.onDriversFiltersNamesOptionsSetAll(newValue);
                break;
            case FILTER_TYPES.EVENTS:
                this.props.onEventsFiltersTypesOptionsSetAll(newValue);
                break;
            default:
                break;
        }
    }

    toggleFilter = (filterType) => {
        switch (filterType) {
            case FILTER_TYPES.VEHICLES:
                this.setState({ isVehiclesFilterExpanded: !this.state.isVehiclesFilterExpanded });
                if(!this.state.allowAllFiltersToExpand){
                    this.setState({ isDriversFilterExpanded: false, isEventsFilterExpanded: false });
                }
                break;
            case FILTER_TYPES.DRIVERS:
                this.setState({ isDriversFilterExpanded: !this.state.isDriversFilterExpanded });
                if(!this.state.allowAllFiltersToExpand){
                    this.setState({ isVehiclesFilterExpanded: false, isEventsFilterExpanded: false });
                }
                break;
            case FILTER_TYPES.EVENTS:
                this.setState({ isEventsFilterExpanded: !this.state.isEventsFilterExpanded });
                if(!this.state.allowAllFiltersToExpand){
                    this.setState({ isVehiclesFilterExpanded: false, isDriversFilterExpanded: false });
                }
                break;
            default:
                break;
        }
    }

    render() {
        const selectedDate = this.props.dateOptions.find((option) => option.checked === true);
        const showTagsFilter = this.props.tagsLoaded && !this.props.tagsError && !this.props.tagsLoading && this.props.tags.length > 0;
        
        const numberOfSelectedVehicles = this.props.filterOptionsVehicleNames.filter((vehicle) => vehicle.checked).length;
        const numberOfSelectedDrivers = this.props.filterOptionsDriverNames.filter((driver) => driver.checked).length;
        const numberOfSelectedEvents = this.props.filterOptionsEvents.filter((event) => event.checked).length;

        return (
            <div className="event-review-filter-group-panel">

                <div className="filter-options-container">

                    {/* Date Range */}
                    <div className="filter-date-wrapper">
                        <div className="review-date-select-wrapper">
                            <div>
                                <div className="review-filter-label">{strings.reports_filter_options_date_range}</div>
                                <div className="show-type">
                                    <SelectDropDown for={'date'} requiredSelection={true} enableFilter={false} options={this.props.dateOptions} singleOnlySelection={true} onChange={this.props.setDateOptions} />
                                </div>
                            </div>
                            {this.needsResizeThrottler && <ResizeThrottler interval={RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS} callback={this.resizeCallback} />}

                            {selectedDate.value === 'custom' && // if custom date range is selected
                                
                                <div>
                                    <DateRangePicker
                                        disabled={false}
                                        startDate={this.props.startDate}
                                        startDateId="your_unique_start_date_id"
                                        endDate={this.props.endDate ? this.props.endDate : new Date()}
                                        endDateId="your_unique_end_date_id"
                                        onDatesChange={this.props.onCustomDateSelect}
                                        focusedInput={this.state.dateFocusedInput}
                                        onFocusChange={focusedInput => this.setState({ dateFocusedInput: focusedInput })}
                                        enableOutsideDays={true}
                                        minimumNights={0}
                                        orientation={this.state.windowWidth > MAX_WIDTH_FOR_DATE_SELECTOR ? "horizontal" : "vertical"}
                                        withFullScreenPortal={this.state.windowWidth > MAX_WIDTH_FOR_DATE_SELECTOR ? false : true}
                                        withPortal={this.state.windowWidth > MAX_WIDTH_FOR_DATE_SELECTOR ? false : true}
                                        isDayBlocked={() => { return false }}
                                        isOutsideRange={this.checkDateIsOutside}
                                        /* the following props are prep for easier CSS styling */
                                        noBorder={true} /* do not wrap a border around this component */
                                        small={true} /* use more compact padding, input size, and arrow */
                                        block={true} /* scale-to-fit parent div width */
                                    />
                                </div>
                            }
                        </div>
                    </div>

                    {/* Vehicles */}
                    <div className={`filter-option-wrapper filter-option-vehicles ${this.state.isVehiclesFilterExpanded ? '' : 'collapsed'}`}>
                        <button className="review-filter-label" onClick={() => this.toggleFilter(FILTER_TYPES.VEHICLES)}>
                            <span>
                                {strings.reports_filter_options_vehicles}
                                {!this.state.isVehiclesFilterExpanded && <small>({strings.formatString(strings.reports_filter_x_selected, numberOfSelectedVehicles)})</small>}
                            </span>
                            <i className="material-icons disclosure">keyboard_arrow_up</i>
                        </button>
                        <div className="checkbox-filters">
                            <button className="btn btn-sm" ref={ref => this.selectAllVehiclesButtonRef = ref} onClick={() => this.changeSelection(FILTER_TYPES.VEHICLES)} >
                                {strings.action_select_all}
                            </button>
                            <button className="btn btn-sm" onClick={() => this.changeSelection(FILTER_TYPES.VEHICLES, false)} >
                                {strings.action_select_none}
                            </button>
                        </div>
                        <div className="tags-filter-wrapper">
                            {showTagsFilter &&
                                <div className="show-tags-filter">
                                    <div className="show-type">{this.renderTagsFilter()}</div>
                                </div>
                            }
                        </div>
                        <div ref={this.vehiclesFilterDropdownRef} className="show-type">
                            <SelectDropDown for={'vehicles'} nameEmbeddedSelectBox={strings.reports_filter_vehicle} requiredSelection={true} enableFilter={false} enableSearch={true} enableSimplifiedClear={false} options={this.props.filterOptionsVehicleNames} onChange={this.props.onVehiclesFiltersNamesOptionSelect} onSetAll={this.props.onVehiclesFiltersNamesOptionsSetAll} />
                        </div>
                    </div>

                    {/* Drivers */}
                    {this.props.features.facial_recognition && // env and account config
                        this.props.facialRecognitionEnabled && // account settings, if available (RAV-4218, if user disables FR, omit the filter)
                        this.props.filterOptionsDriverNames && // driver names from Reporting API
                        this.props.filterOptionsDriverNames.length > 1 && // driver names contains more than just "Unknown" option (RAV-4468 if FR enabled/disabled state cannot be determined than check filter length)

                        <div className={`filter-option-wrapper filter-option-drivers ${this.state.isDriversFilterExpanded ? '' : 'collapsed'}`}>
                            <button className="review-filter-label" onClick={() => this.toggleFilter(FILTER_TYPES.DRIVERS)}>
                                <span>
                                    {strings.reports_filter_options_drivers}
                                    {!this.state.isDriversFilterExpanded && <small>({strings.formatString(strings.reports_filter_x_selected, numberOfSelectedDrivers)})</small>}
                                </span>
                                <i className="material-icons disclosure">keyboard_arrow_up</i>
                            </button>
                            <div className="checkbox-filters">
                                <button className="btn btn-sm" ref={ref => this.selectAllDriversButtonRef = ref} onClick={() => this.changeSelection(FILTER_TYPES.DRIVERS)} >
                                    {strings.action_select_all}
                                </button>
                                <button className="btn btn-sm" onClick={() => this.changeSelection(FILTER_TYPES.DRIVERS, false)} >
                                    {strings.action_select_none}
                                </button>
                            </div>
                            <div ref={this.driversFilterDropdownRef} className="show-type">
                                <SelectDropDown for={'drivers'} nameEmbeddedSelectBox={strings.reports_filter_driver} requiredSelection={true} enableFilter={false} enableSearch={true} enableSimplifiedClear={false} options={this.props.filterOptionsDriverNames} onChange={this.props.onDriversFiltersNamesOptionSelect} onSetAll={this.props.onDriversFiltersNamesOptionsSetAll} />
                            </div>
                        </div>
                    }

                    {/* Events */}
                    <div className={`filter-option-wrapper filter-option-events ${this.state.isEventsFilterExpanded ? '' : 'collapsed'}`}>
                        <button className="review-filter-label" onClick={() => this.toggleFilter(FILTER_TYPES.EVENTS)}>
                            <span>
                                {strings.reports_filter_options_event_filters}
                                {!this.state.isEventsFilterExpanded && <small>({strings.formatString(strings.reports_filter_x_selected, numberOfSelectedEvents)})</small>}
                            </span>
                            <i className="material-icons disclosure">keyboard_arrow_up</i>
                        </button>
                        
                        <div className="checkbox-filters">
                            <button className="btn btn-sm" ref={ref => this.selectAllEventsButtonRef = ref} onClick={() => this.changeSelection(FILTER_TYPES.EVENTS)} >
                                {strings.action_select_all}
                            </button>
                            <button className="btn btn-sm" onClick={() => this.changeSelection(FILTER_TYPES.EVENTS, false)} >
                                {strings.action_select_none}
                            </button>
                        </div>
                        <div ref={this.eventsFilterDropdownRef} className="show-type">
                            <SelectDropDown for={'events'} nameEmbeddedSelectBox={strings.reports_filter_event} requiredSelection={true} enableFilter={false} enableSearch={true} enableSimplifiedClear={false} options={this.props.filterOptionsEvents} onChange={this.props.onEventsFiltersTypesOptionSelect} onSetAll={this.props.onEventsFiltersTypesOptionsSetAll} />
                        </div>
                        <div ref={this.flagFilterDropdownRef} className="show-type show-type-flag">
                            <FlagFilterDropDown optionsWithClickState={this.props.filterOptionsFlags} onChangeClickState={this.props.onEventsFiltersFlagsOptionSelect} />
                        </div> 
                    </div>
                </div>

                <div className="filter-search">
                    <button className="clear-button" disabled={this.props.clearIsDisabled} onClick={this.props.onClickClear}>{strings.action_clear_filters}</button>
                    <button ref={ref => this.searchButtonRef = ref} disabled={this.props.applyIsDisabled} className="btn" onClick={this.props.onClickSearch}>{strings.action_apply}</button>
                </div>

            </div>
        )
    }
}

FilterGroupPanel.propTypes = {
    features: PropTypes.shape({
        facial_recognition: PropTypes.bool
    }).isRequired,
    facialRecognitionEnabled: PropTypes.bool,
    user: PropTypes.shape({
        permissions: PropTypes.array
    }),
    match: PropTypes.shape({
        params: PropTypes.shape({
            parentGroupingType: PropTypes.string
        })
    }),
    dateOptions: PropTypes.array.isRequired,
    selectedDateIndex: PropTypes.number.isRequired,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    filterOptionsVehicleNames: PropTypes.array.isRequired,
    filterOptionsDriverNames: PropTypes.array,
    filterOptionsEvents: PropTypes.array.isRequired,
    filterOptionsFlags: PropTypes.array,
    tags: PropTypes.array,
    tagsLoaded: PropTypes.bool,
    tagsLoading: PropTypes.bool,
    tagsError: PropTypes.bool,
    clearFilterTags: PropTypes.bool,
    sortOptions: PropTypes.any,
    applyIsDisabled: PropTypes.bool,
    clearIsDisabled: PropTypes.bool,

    // Callbacks
    onCustomDateSelect: PropTypes.func.isRequired,
    setDateOptions: PropTypes.func.isRequired,
    onVehiclesFiltersNamesOptionSelect: PropTypes.func.isRequired,
    onVehiclesFiltersNamesOptionsSetAll: PropTypes.func.isRequired,
    onDriversFiltersNamesOptionSelect: PropTypes.func.isRequired,
    onDriversFiltersNamesOptionsSetAll: PropTypes.func.isRequired,
    onEventsFiltersTypesOptionSelect: PropTypes.func.isRequired,
    onEventsFiltersTypesOptionsSetAll: PropTypes.func.isRequired,
    onEventsFiltersFlagsOptionSelect: PropTypes.func,
    onTagsFilterOptionsSelect: PropTypes.func.isRequired,
    resetClearFilterTags: PropTypes.func.isRequired,
    onClickClear: PropTypes.func.isRequired,
    onClickSearch: PropTypes.func.isRequired
};


const mapStateToProps = (state, ownProps) => {
    return {
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterGroupPanel);
